/**
 * Direcciones guardadas del usuario (MVP-02B §3).
 *
 * Se carga por separado de la geografía a propósito: cuando ambas viajaban en
 * el mismo `Promise.all`, un fallo aquí dejaba también sin provincias al
 * formulario y el mensaje de error solo hablaba de direcciones.
 */
import { useCallback, useEffect, useState } from "react";

import { createDireccion, deleteDireccion, fetchDirecciones, updateDireccion } from "./api.js";

export function useDirecciones({ enabled = true } = {}) {
    const [recurso, setRecurso] = useState({ state: "loading", data: [], error: null });

    const recargar = useCallback(() => {
        if (!enabled) {
            setRecurso({ state: "empty", data: [], error: null });
            return Promise.resolve([]);
        }
        setRecurso((prev) => ({ ...prev, state: "loading", error: null }));
        return fetchDirecciones()
            .then((data) => {
                setRecurso({ state: data.length ? "ready" : "empty", data, error: null });
                return data;
            })
            .catch((error) => {
                setRecurso({ state: "error", data: [], error });
                return [];
            });
    }, [enabled]);

    useEffect(() => {
        recargar();
    }, [recargar]);

    /**
     * Crea una dirección y la devuelve ya incorporada a la lista local, para
     * que checkout pueda usarla de inmediato sin recargar la página.
     */
    const crear = useCallback(async (direccion) => {
        const creada = await createDireccion(direccion);
        setRecurso((prev) => {
            const data = prev.data.map((item) =>
                creada.predeterminado ? { ...item, predeterminado: false } : item,
            );
            return { state: "ready", data: [...data, creada], error: null };
        });
        return creada;
    }, []);

    const actualizar = useCallback(async (id, direccion) => {
        const actualizada = await updateDireccion(id, direccion);
        setRecurso((prev) => ({
            ...prev,
            state: "ready",
            data: prev.data.map((item) => {
                if (Number(item.id) === Number(id)) return actualizada;
                return actualizada.predeterminado ? { ...item, predeterminado: false } : item;
            }),
        }));
        return actualizada;
    }, []);

    const borrar = useCallback(async (id) => {
        await deleteDireccion(id);
        setRecurso((prev) => {
            const data = prev.data.filter((item) => Number(item.id) !== Number(id));
            return { state: data.length ? "ready" : "empty", data, error: null };
        });
    }, []);

    const predeterminada = recurso.data.find((item) => item.predeterminado) ?? recurso.data[0] ?? null;

    return { direcciones: recurso, predeterminada, recargar, crear, actualizar, borrar };
}
